import { CameraAlt, Circle, Close, Delete, Done, Image, RotateLeft } from '@mui/icons-material';
import {
  Avatar,
  Dialog,
  DialogContent,
  DialogProps,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText
} from '@mui/material';
import { FC, useCallback, useRef } from 'react';
import Webcam from 'react-webcam';

import { CAPTURE_VIDEO_CONSTRAINTS } from '../capture-constraints';

import { TransactionImage } from 'types';
import { ImageOverlay } from 'utils';
import { ImagesProvider, useImages } from '.';
import { useNewTransaction } from '..';
import './image-dialog.scss';

export const ImageCapture: FC<{ addImage: (src: string) => void }> = ({ addImage }) => {
  const { webcam, setPreview, setWebcam } = useImages();
  const webcamRef = useRef<Webcam>(null);
  const { container, chassis, genset, stack, bulk } = useNewTransaction();

  const handleCapture = useCallback(async () => {
    const text = bulk
      ? (chassis.unitNumber ?? '') +
        ' ' +
        stack
          .map(u => u.unitNumber ?? '')
          .slice(0, 3)
          .join(' ')
      : [container.unitNumber ?? '', chassis.unitNumber ?? ' ', genset.unitNumber ?? ' '].join(' ');
    ImageOverlay.withText(text + ' ' + new Date().toLocaleString())
      .withSetter(img => {
        if (img) {
          addImage(img);
          setPreview(img);
        }
      })
      .call(Webcam.prototype.getScreenshot.call(webcamRef.current));
  }, [addImage, setPreview, bulk, stack, container.unitNumber, chassis.unitNumber, genset.unitNumber]);

  return (
    <Dialog fullWidth className="image-dialog" open={webcam} onClose={() => setWebcam(false)}>
      <DialogTitle className="d-flex align-items-center justify-content-between">
        <p className="font-weight-bold">NEW IMAGE</p>
        <IconButton onClick={() => setWebcam(false)}>
          <Close fontSize="large" />
        </IconButton>
      </DialogTitle>
      <DialogContent className="webcam">
        <Webcam
          width="100%"
          ref={webcamRef}
          videoConstraints={CAPTURE_VIDEO_CONSTRAINTS}
          screenshotFormat="image/jpeg"
        />
        <IconButton onClick={handleCapture}>
          <Circle />
        </IconButton>
      </DialogContent>
    </Dialog>
  );
};

export const ImagePreview: FC<{
  images: Partial<TransactionImage> & Required<Pick<TransactionImage, 'imageBase64'>>[];
  deleteImage: (src: string) => void;
  immutable?: boolean;
}> = ({ images, deleteImage, immutable }) => {
  const { preview, webcam, setPreview, setWebcam } = useImages();

  const handleClose = useCallback(() => {
    setPreview(undefined);
    setWebcam(false);
  }, [setPreview, setWebcam]);

  const handleDelete = useCallback(() => {
    if (preview) deleteImage(preview);
    handleClose();
  }, [preview, deleteImage, handleClose]);

  const handleRetake = useCallback(() => {
    if (preview) deleteImage(preview);
    setPreview(undefined);
  }, [preview, deleteImage, setPreview]);

  if (!preview) return null;

  return (
    <Dialog fullWidth className="image-dialog new-image" maxWidth="sm" open={!!preview} onClose={handleClose}>
      <DialogTitle className="d-flex align-items-center justify-content-between">
        <p className="font-weight-bold">IMAGE #{images.findIndex(({ imageBase64 }) => imageBase64 === preview) + 1}</p>
        <div className="flex-fill d-flex justify-content-between">
          <IconButton onClick={handleDelete} disabled={immutable}>
            {!immutable && <Delete fontSize="large" />}
          </IconButton>
          <IconButton onClick={handleClose}>
            <Done fontSize="large" />
          </IconButton>
        </div>
      </DialogTitle>
      <DialogContent className="preview">
        <img alt="preview" width="100%" src={preview} />
        {!!webcam && (
          <IconButton onClick={handleRetake}>
            <RotateLeft fontSize="large" />
          </IconButton>
        )}
      </DialogContent>
    </Dialog>
  );
};

export const UploadImages: FC<
  DialogProps & {
    images: Partial<TransactionImage> & Required<Pick<TransactionImage, 'imageBase64'>>[];
    addImage: (src: string) => void;
    deleteImage: (src: string) => void;
  }
> = ({ images, addImage, deleteImage, ...props }) => {
  const { setWebcam, setPreview } = useImages();

  const readFile = (file: File) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = err => reject(err);
      reader.readAsDataURL(file);
    });

  const saveImage = async (file: File) => {
    try {
      const result = await readFile(file);
      if (typeof result !== 'string') return;
      addImage(result);
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <Dialog fullWidth className="image-dialog" maxWidth="sm" {...props}>
      <DialogTitle className="d-flex align-items-center justify-content-between">
        <p className="font-weight-bold">UPLOAD IMAGE</p>
        <IconButton onClick={props.onClose as () => any}>
          <Close fontSize="large" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <div className="input-wrapper" onClick={() => setWebcam(true)}>
          <label className="font-weight-bold">Use Camera</label>
          <CameraAlt color="info" fontSize="large" />
          <div className="upload-wrapper">
            <p>Or upload files from your device</p>
            <input
              type="file"
              accept="image/jpeg"
              capture="environment"
              onClick={e => e.stopPropagation() /* do not open camera */}
              onChange={e => Array.prototype.forEach.call((e.target as HTMLInputElement).files, saveImage)}
            />
          </div>
        </div>
        <List dense>
          {!images.length ? (
            <ListItem>
              <ListItemAvatar>
                <Avatar>
                  <Image />
                </Avatar>
              </ListItemAvatar>
              <ListItemText primary="No files" />
            </ListItem>
          ) : (
            images.map(({ imageBase64 }, i) => (
              <ListItem
                key={i}
                secondaryAction={
                  <IconButton edge="end" onClick={() => deleteImage(imageBase64)}>
                    <Delete />
                  </IconButton>
                }
              >
                <ListItemAvatar onClick={() => setPreview(imageBase64)}>
                  <Avatar>
                    <Image />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText primary={`IMAGE #${i + 1}`} />
              </ListItem>
            ))
          )}
        </List>
      </DialogContent>
    </Dialog>
  );
};

const ImagesConsumer: FC<DialogProps> = props => {
  const { preview, webcam } = useImages();
  const { images, addImage, deleteImage } = useNewTransaction();
  if (preview) return <ImagePreview images={images} deleteImage={deleteImage} />;
  if (webcam) return <ImageCapture addImage={addImage} />;
  return <UploadImages images={images} addImage={addImage} deleteImage={deleteImage} {...props} />;
};

const ImageDialog: FC<DialogProps> = props => (
  <ImagesProvider>
    <ImagesConsumer {...props} />
  </ImagesProvider>
);

export default ImageDialog;
